import React, { useEffect, useState } from "react";
import { fetchNotifications, markNotificationsAsSeen } from "../../../services/Notification/NotificationApi";
import { useFeatures } from "../../../context/PermissionsAndFeatures/FeaturesContext";

// ✅ دالة ترجمة أنواع الإشعارات إلى العربية
const translateNotificationType = (type) => {
  const translations = {
    "between_users": "بين المستخدمين",
    "order_created": "طلب جديد",
    "order_updated": "تحديث طلب",
    "order_completed": "طلب مكتمل",
    "order_cancelled": "طلب ملغى",
    "payment_received": "دفعة مستلمة",
    "new_message": "رسالة جديدة",
    "system_alert": "تنبيه النظام",
    "reservation": "حجز",
    "delivery": "توصيل",
    "kitchen": "مطبخ",
    "general": "عام",
  };
  return translations[type] || type;
};

function NotificationList({ token, onViewAll }) {
  // استدعاء hook الخاص بالميزات (غير مشروط)
  const features = useFeatures();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const resolveFeatureFlag = (featObj, key) => {
    if (featObj == null) return null; // unknown
    try {
      if (typeof featObj === "function") return featObj(key);
      if (typeof featObj.isEnabled === "function") return featObj.isEnabled(key);
      if (typeof featObj.hasFeature === "function") return featObj.hasFeature(key);
      if (typeof featObj.get === "function") return featObj.get(key);
      if (key in featObj) return featObj[key];
    } catch (e) {
      return null;
    }
    return null;
  };

  // compute flag (لا يُستخدم للـ early return هنا لتجنب استدعاء Hooks بشكلٍ شرطي)
  const rawNotificationsFlag = resolveFeatureFlag(features, "notifications");
  const isNotificationsEnabled = rawNotificationsFlag === false ? false : true;

  // دالة مُعاد استخدامها لجلب الإشعارات وتحديث الحالة (تُستخدم أيضاً بعد تعليم الكل كمقروء)
  const loadNotifications = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchNotifications(token);
      const notifications = (res?.data?.notifications || [])
        .slice()
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setNotifications(notifications);
    } catch (err) {
      setError("فشل في جلب الإشعارات");
    } finally {
      setLoading(false);
    }
  };

  // useEffect يُعرف هنا (غير مشروط) ويعتمد على isNotificationsEnabled لعدم التنفيذ إذا كانت الميزة معطّلة
  useEffect(() => {
    if (token && isNotificationsEnabled) loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isNotificationsEnabled]);

  const handleMarkAsSeen = async (id) => {
    try {
      await markNotificationsAsSeen(token, [id]);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, seen: 1 } : n))
      );
    } catch {
      // يمكن إضافة إشعار بالخطأ إذا رغبت
    }
  };

  const isDark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

  const containerStyle = {
    minWidth: 320,
    background: isDark ? "#23272f" : "#fff",
    borderRadius: 10,
    boxShadow: isDark
      ? "0 2px 12px #000a"
      : "0 2px 8px #0001",
    padding: 18,
    color: isDark ? "#f1f1f1" : "#222",
    border: isDark ? "1px solid #333" : "1px solid #eee",
    maxHeight: 420,
    overflowY: "auto",
    transition: "background 0.2s"
  };

  const titleStyle = {
    margin: "0 0 14px 0",
    fontSize: "1.13rem",
    fontWeight: "bold",
    letterSpacing: ".5px",
    color: isDark ? "#fff" : "#222"
  };

  const itemStyle = (seen) => ({
    background: seen ? (isDark ? "#23272f" : "#f7f7f7") : (isDark ? "#26334d" : "#e3f2fd"),
    borderRadius: 7,
    marginBottom: 10,
    padding: "12px 14px",
    borderRight: seen
      ? (isDark ? "4px solid #444" : "4px solid #bbb")
      : "4px solid #1976d2",
    boxShadow: isDark ? "0 1px 4px #0002" : "0 1px 4px #0001",
    transition: "background 0.2s"
  });

  const msgStyle = {
    fontSize: ".97rem",
    color: isDark ? "#d6e0f0" : "#333"
  };

  const dateStyle = {
    fontSize: ".85rem",
    color: isDark ? "#aaa" : "#888",
    marginTop: 4
  };

  // عند الضغط على "رؤية الكل" نرسل جميع الـ ids للتنبيهات غير المقروءة إلى الـ API
  const handleViewAll = async () => {
    if (!token) {
      if (onViewAll) onViewAll();
      return;
    }

    const unseenIds = notifications
      .filter((n) => !n.seen || n.seen === 0)
      .map((n) => n.id);

    if (unseenIds.length === 0) {
      if (onViewAll) onViewAll();
      else await loadNotifications();
      return;
    }

    const prev = notifications;

    setNotifications((prevList) =>
      prevList.map((n) => (unseenIds.includes(n.id) ? { ...n, seen: 1 } : n))
    );

    try {
      await markNotificationsAsSeen(token, unseenIds);
      await loadNotifications();
      if (onViewAll) onViewAll();
    } catch (err) {
      setNotifications(prev);
      alert("فشل في تعليم التنبيهات كمقروءة. حاول مرة أخرى.");
    }
  };

  // بعد تعريف جميع الـ hooks والدوال: إن كانت الميزة معطّلة نعرض رسالة تفاعلية تطلب التواصل مع الدعم الفني
  if (isNotificationsEnabled === false) {
    return (
      <div style={containerStyle}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <h3 style={titleStyle}>الإشعارات</h3>
          <div
            style={{
              maxWidth: 520,
              width: "100%",
              textAlign: "center",
              padding: 14,
              borderRadius: 8,
              background: isDark ? "#3a2b2b" : "#fff3cd",
              color: isDark ? "#ffe8b8" : "#856404",
              border: isDark ? "1px solid #5a473a" : "1px solid #ffeeba",
              boxShadow: isDark ? "0 1px 6px #0003" : "0 1px 4px #0001",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 6 }}>🚫 خدمة الإشعارات غير مفعّلة</div>
            <div>يرجى التواصل مع فريق الدعم الفني لتفعيل ميزة الإشعارات.</div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div style={{ padding: 16 }}>جاري التحميل...</div>;
  if (error) return <div style={{ color: "red", padding: 16 }}>{error}</div>;

  return (
    <div style={containerStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <h3 style={titleStyle}>الإشعارات</h3>
        <button
          onClick={handleViewAll}
          style={{
            background: "#1976d2",
            color: "#fff",
            border: "none",
            padding: "6px 10px",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "0.95rem"
          }}
          title="رؤية كل الإشعارات"
        >
          رؤية الكل
        </button>
      </div>
      {notifications.length === 0 && <div>لا توجد إشعارات.</div>}
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {notifications.map((n) => (
          <li key={n.id} style={itemStyle(n.seen)}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: "bold", marginBottom: 4 }}>{n.title}</div>
              {!n.seen && (
                <button
                  onClick={() => handleMarkAsSeen(n.id)}
                  title="تعليم كمقروء"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#43a047",
                    fontSize: "1.2em",
                    marginRight: 6,
                  }}
                >
                  ✔
                </button>
              )}
            </div>
            <div style={msgStyle}>{n.message}</div>
            <div style={{ marginTop: 8, fontSize: ".9rem", color: isDark ? "#b0bec5" : "#555" }}>
              <div>المرسل (from_user_id): {n.from_user_id ?? "-"}</div>
              <div>رقم الطلب (order_id): {n.order_id ?? "-"}</div>
              <div>النوع: {n.type ? translateNotificationType(n.type) : "-"}</div>
            </div>
            <div style={dateStyle}>
              {new Date(n.created_at).toLocaleString()}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default NotificationList;
